import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CursorEffects = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [trails, setTrails] = useState([]);

  useEffect(() => {
    let trailTimeout;
    
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });

      // Add trail effect
      const newTrail = {
        id: Date.now() + Math.random(),
        x: e.clientX,
        y: e.clientY,
      };
      
      setTrails(prev => [...prev.slice(-8), newTrail]);

      // Clear old trails
      trailTimeout = setTimeout(() => {
        setTrails(prev => prev.slice(1));
      }, 100);
    };

    const handleMouseEnter = (e) => {
      const target = e.target;
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a') ||
        target.classList.contains('cursor-pointer')
      ) {
        setIsHovering(true);
      }
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseEnter, true);
    document.addEventListener('mouseout', handleMouseLeave, true);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseEnter, true);
      document.removeEventListener('mouseout', handleMouseLeave, true);
      clearTimeout(trailTimeout);
    };
  }, []);

  // Don't render on touch devices
  if ('ontouchstart' in window) {
    return null;
  }

  return (
    <div className="hidden lg:block">
      {/* Custom Cursor Ring */}
      <motion.div
        className="fixed w-8 h-8 border-2 border-primary/50 rounded-full pointer-events-none z-[9999]"
        style={{
          left: mousePosition.x - 16,
          top: mousePosition.y - 16,
        }}
        animate={{
          scale: isHovering ? 1.5 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 28,
        }}
      />

      {/* Inner Dot */}
      <motion.div
        className="fixed w-2 h-2 bg-primary/70 rounded-full pointer-events-none z-[9999]"
        style={{
          left: mousePosition.x - 4,
          top: mousePosition.y - 4,
        }}
        transition={{
          type: "spring",
          stiffness: 800,
          damping: 28,
        }}
      />

      {/* Cursor Trails */}
      <AnimatePresence>
        {trails.slice(0, 5).map((trail, index) => (
          <motion.div
            key={trail.id}
            className="fixed top-0 left-0 w-1 h-1 rounded-full pointer-events-none z-[9998]"
            initial={{
              x: trail.x,
              y: trail.y,
              opacity: 0.5,
              scale: 1,
            }}
            animate={{
              opacity: 0,
              scale: 0,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.4,
              ease: 'easeOut',
            }}
            style={{
              backgroundColor: `rgba(147, 51, 234, ${0.5 - index * 0.08})`,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Glow Effect on Hover */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            className="fixed top-0 left-0 pointer-events-none z-[9997]"
            initial={{ opacity: 0 }}
            animate={{
              x: mousePosition.x - 40,
              y: mousePosition.y - 40,
              opacity: 0.25,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-20 h-20 rounded-full bg-purple-500 blur-xl" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CursorEffects;
